/**
 * blocked.js — is a milestone waiting on a predecessor? (Matt, 2026-09-24)
 *
 * "If a milestone has a predecessor, it should not be able to begin until that predecessor is either
 * completed (posted), deleted, or unlinked." A BLOCKED milestone is visible for information only: its
 * checklist, documents, posting and launch are locked; its kit (project facts, gathered ahead on
 * purpose), its Slack discussion and its schedule actions (dates, unlink, delete, move) stay open,
 * since those are how it gets unblocked. The outline shows it dimmed with a lock and "waiting on ___",
 * distinct from done (gray, struck through).
 *
 * The one client-side answer, read by the outline row, the zoom title, the milestone sections, the
 * checklist write paths in outline.js and the post window. The launch refuses the same case on the
 * server (api/flights.js#launchCheck). Predecessors come from the milestone record's own
 * `Predecessor` links; each is looked up among the milestones the page has loaded (the zoomed
 * project's, or any project open on the home list). A predecessor the page has not loaded does not
 * block here — never lock work on data we cannot see; the server check still holds the launch.
 */
import { ctx } from './ctx.js'
import { isStartedAhead } from './milestoneFlow.js'

function findMs(id) {
  const lists = [ctx.state && ctx.state.milestones]
  const cache = ctx.milestonesCache
  if (cache) for (const k of Object.keys(cache)) lists.push(cache[k])
  for (const list of lists) {
    if (!Array.isArray(list)) continue
    for (const m of list) if (m && m.id === id) return m
  }
  return null
}

export function msLabel(rec) {
  const f = (rec && rec.fields) || {}
  return f['Full Deliverable Name'] || f['Name'] || 'another milestone'
}

/** The open predecessors (records) holding this milestone back; [] when it may begin. */
export function blockedBy(msOrId) {
  const ms = typeof msOrId === 'string' ? findMs(msOrId) : msOrId
  if (!ms || !ms.fields || ms.fields['Posted Date']) return []
  const out = []
  for (const id of ms.fields['Predecessor'] || []) {
    // A firm leader let it start ahead of this one (docs/COMPRESSED_CHAIN.md): the link stays, it no
    // longer holds the milestone back.
    if (isStartedAhead(ms.id, id)) continue
    const p = findMs(id)
    if (p && p.fields && !p.fields['Posted Date']) out.push(p)
  }
  return out
}

export function isBlocked(msOrId) { return blockedBy(msOrId).length > 0 }

/** "Agency Review - PPC 1st Submission" / "A and B" — for the tag and the refusal messages. */
export function waitingOnText(msOrId) {
  const names = blockedBy(msOrId).map(msLabel)
  if (names.length <= 1) return names[0] || ''
  return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1]
}

/** The refusal every locked action shows — just what it waits on; no instructions (Matt: a to-do in
 *  the message suggests anyone on the team should resolve it). */
export function blockedMessage(msOrId) {
  const w = waitingOnText(msOrId)
  return w ? `Waiting on ${w}.` : ''
}
